import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ChevronLeftIcon, ChevronRightIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { RootState } from '../../store';
import { i18nService } from '../../services/i18n';
import { logService } from '../../services/logService';

import { setViewMode, selectLog, setSubjects } from '../../store/slices/logSlice';
import LogCalendar from './LogCalendar';
import LogList from './LogList';
import LogDetail from './LogDetail';
import LogForm from './LogForm';
import SubjectForm from './SubjectForm';
import SubjectList from './SubjectList';
import type { LogManagerViewMode } from '../../store/slices/logSlice';
import type { Log, Subject } from '../../types/log';

interface LogManagerViewProps {
  onStartLogAssistant: () => void;
}

const LogManagerView: React.FC<LogManagerViewProps> = (props) => {
  const { onStartLogAssistant } = props;
  const dispatch = useDispatch();
  const { logs, subjects, selectedSubjectIds, selectedLogId, viewMode, loading, error } = useSelector(
    (state: RootState) => state.logManager
  );
  const [showLogForm, setShowLogForm] = useState(false);
  const [editingLog, setEditingLog] = useState<Log | null>(null);
  const [showSubjectForm, setShowSubjectForm] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [subjectSidebarExpanded, setSubjectSidebarExpanded] = useState(true);

  useEffect(() => {
    logService.init();
    loadSubjects();
    loadLogs();
    return () => {
      logService.destroy();
    };
  }, []);

  const loadSubjects = async () => {
    const subjectList = await logService.loadSubjects();
    dispatch(setSubjects(subjectList));
  };

  const loadLogs = async () => {
    await logService.loadLogs();
  };

  const handleViewModeChange = (mode: LogManagerViewMode) => {
    dispatch(setViewMode(mode));
  };

  const handleSelectLog = (logId: string | null) => {
    dispatch(selectLog(logId));
  };

  const handleNewLog = () => {
    setEditingLog(null);
    setShowLogForm(true);
  };

  const handleEditLog = (log: Log) => {
    setEditingLog(log);
    setShowLogForm(true);
  };

  const handleLogCreated = () => {
    setShowLogForm(false);
    setEditingLog(null);
  };

  const handleNewSubject = () => {
    setEditingSubject(null);
    setShowSubjectForm(true);
  };

  const handleEditSubject = (subject: Subject) => {
    setEditingSubject(subject);
    setShowSubjectForm(true);
  };

  const handleSubjectCreated = () => {
    setShowSubjectForm(false);
    setEditingSubject(null);
    loadSubjects();
  };

  const toggleSubjectSidebar = () => {
    setSubjectSidebarExpanded(!subjectSidebarExpanded);
  };

  const selectedLog = selectedLogId
    ? logs.find((l) => l.id === selectedLogId) || null
    : null;

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden dark:bg-claude-darkBg bg-claude-bg">
      {/* Header - Title only */}
      <div className="shrink-0 px-6 py-4 border-b dark:border-claude-darkBorder border-claude-border">
        <h1 className="text-xl font-semibold dark:text-claude-darkText text-claude-text">
          {i18nService.t('logManagerTitle') || 'Log Manager'}
        </h1>
      </div>

      {/* Navigation Bar */}
      <div className="shrink-0 flex items-center gap-4 px-6 py-3 border-b dark:border-claude-darkBorder border-claude-border">
        {/* View Mode Toggle */}
        <div className="flex rounded-lg border dark:border-claude-darkBorder border-claude-border overflow-hidden">
          <button
            type="button"
            onClick={() => handleViewModeChange('calendar')}
            className={`px-4 py-1.5 text-sm font-medium transition-colors ${
              viewMode === 'calendar'
                ? 'dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover dark:text-claude-darkText text-claude-text'
                : 'dark:text-claude-darkTextSecondary text-claude-textSecondary hover:dark:text-claude-darkText hover:text-claude-text'
            }`}
          >
            {i18nService.t('calendar') || 'Calendar'}
          </button>
          <button
            type="button"
            onClick={() => handleViewModeChange('list')}
            className={`px-4 py-1.5 text-sm font-medium transition-colors ${
              viewMode === 'list'
                ? 'dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover dark:text-claude-darkText text-claude-text'
                : 'dark:text-claude-darkTextSecondary text-claude-textSecondary hover:dark:text-claude-darkText hover:text-claude-text'
            }`}
          >
            {i18nService.t('list') || 'List'}
          </button>
        </div>

        <button
          type="button"
          onClick={onStartLogAssistant}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-claude-accent text-white hover:bg-claude-accent/90 transition-colors"
        >
          <ChatBubbleLeftRightIcon className="h-4 w-4" />
          Log Assistant
        </button>

        {/* New Log Button - Show for calendar and list views */}
        <button
          type="button"
          onClick={handleNewLog}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-claude-accent text-white hover:bg-claude-accent/90 transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {i18nService.t('logManagerNewLog') || 'New Log'}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {error && (
          <div className="absolute top-20 left-1/2 -translate-x-1/2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm z-50">
            {error}
          </div>
        )}

        {/* Subject Sidebar */}
        <div
          className={`border-r dark:border-claude-darkBorder border-claude-border overflow-y-auto transition-all duration-300 ${
            subjectSidebarExpanded ? 'w-64' : 'w-0'
          }`}
        >
          <div className={subjectSidebarExpanded ? '' : 'hidden'}>
            <SubjectList
              onNewSubject={handleNewSubject}
              onEditSubject={handleEditSubject}
            />
          </div>
        </div>

        {/* Subject Sidebar Toggle Button - Fixed position */}
        <button
          type="button"
          onClick={toggleSubjectSidebar}
          className="absolute top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-r-lg bg-claude-surface dark:bg-claude-darkSurface border border-l-0 dark:border-claude-darkBorder border-claude-border hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
          style={{ left: subjectSidebarExpanded ? '256px' : '0' }}
          title={subjectSidebarExpanded ? 'Collapse subjects' : 'Expand subjects'}
        >
          {subjectSidebarExpanded ? (
            <ChevronLeftIcon className="h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
          )}
        </button>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-claude-accent" />
            </div>
          ) : viewMode === 'calendar' ? (
            <LogCalendar logs={logs} subjects={subjects} selectedSubjectIds={selectedSubjectIds} onSelectLog={handleSelectLog} />
          ) : (
            <LogList
              logs={logs}
              onSelectLog={handleSelectLog}
              onEditLog={handleEditLog}
            />
          )}
        </div>

        {/* Log Detail Modal */}
        {selectedLog && !showLogForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => handleSelectLog(null)} />
            <div className="relative w-full max-w-md mx-4 bg-white dark:bg-claude-darkSurface rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
              <LogDetail
                log={selectedLog}
                onClose={() => handleSelectLog(null)}
                onEdit={() => handleEditLog(selectedLog)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Log Form Modal */}
      {showLogForm && (
        <LogForm
          log={editingLog}
          onClose={handleLogCreated}
          onSuccess={handleLogCreated}
        />
      )}

      {/* Subject Form Modal */}
      {showSubjectForm && (
        <SubjectForm
          subject={editingSubject}
          onClose={handleSubjectCreated}
          onSuccess={handleSubjectCreated}
        />
      )}
    </div>
  );
};

export default LogManagerView;

export { LogManagerView };